import { useCallback, useMemo } from "react";
import _ from "lodash";

import { URLS } from "config/urls";
import { MoreVertIcon } from "icons";
import { colors } from "theme/palette";
import { reverse } from "utils/urls";

import Button from "components/Button";
import ButtonDropdown from "components/ButtonDropdown";
import GroupLabel from "components/ButtonDropdown/GroupLabel";
import Link from "components/Link";
import { MenuItem } from "components/Menu";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast, { DEFAULT_ERROR_MESSAGE } from "components/Toast";

import { useDealChecklistPermission } from "entities/Deal/sdk";
import FieldUpdateIcon from "entities/Field/components/FieldUpdateIcon";
import {
  fieldApproveBulk,
  fieldMarkAsSeenBulk,
  fieldUpdateNAStatusBulk,
  IPackageTab,
  sectionApproveBulk,
  toggleCategoryApproval,
  useCompanyPackageWithTabsInformation,
} from "entities/Package/sdk";

interface ILeftMenuLink {
  tab: {
    id: number;
    name: string;
    updated_field_count: number;
  };
  packageId: number;
  dealId: number;
  isSelected: boolean;
  fullTabData?: IPackageTab;
  buttonDataTestid?: string;
  linkDataTestid?: string;
  handlePackageUpdate?: ({ message }: { message?: string }) => void;
}

const LeftMenuLink = ({
  tab,
  fullTabData,
  dealId,
  packageId,
  isSelected,
  buttonDataTestid,
  linkDataTestid,
  handlePackageUpdate,
}: ILeftMenuLink) => {
  const { data: userPermission } = useDealChecklistPermission({
    dealId,
    packageId,
  });

  const { mutate: mutateChecklistWithTabs } =
    useCompanyPackageWithTabsInformation({
      dealId,
      packageId,
    });

  const canSeeActionsMenu = useMemo(
    () => !_.isNil(userPermission) && userPermission.is_full_access,
    [userPermission]
  );

  const areAllNA = useMemo(
    () =>
      _.every(
        _.flatMap(fullTabData?.categories, (category) =>
          _.flatMap(category?.sections, (section) =>
            _.map(section?.fields, { n_a_status: true })
          )
        )
      ),
    [fullTabData]
  );

  const areAllApproved = useMemo(
    () =>
      _.every(
        fullTabData?.categories,
        (category) =>
          category.is_approved &&
          _.every(
            category.sections,
            (section) =>
              section.is_approved &&
              _.every(
                section.fields.filter((field) => field.n_a_status === false),
                (field) => field.is_completed
              )
          )
      ),
    [fullTabData]
  );

  const handleMarkAsNA = useCallback(() => {
    const fieldIds = _.flatMap(fullTabData?.categories, (category) =>
      _.flatMap(category?.sections, (section) =>
        _.map(section?.fields, "field_id")
      )
    );

    fieldUpdateNAStatusBulk({
      dealId,
      fieldIds,
      naStatus: !areAllNA,
    }).then(() => {
      handlePackageUpdate?.({
        message: `${fullTabData?.name} was successfully ${
          !areAllNA ? "disabled" : "enabled"
        }!`,
      });
    });
  }, [dealId, fullTabData, areAllNA, handlePackageUpdate]);

  const handleApprove = useCallback(() => {
    const fieldIds = _.flatMap(fullTabData?.categories, (category) =>
      _.flatMap(category?.sections, (section) =>
        _.map(
          section?.fields.filter((field) => field.n_a_status === false),
          "field_id"
        )
      )
    );

    const sectionIds = _.flatMap(fullTabData?.categories, (category) =>
      _.map(category?.sections, "section_id")
    );

    const categoryIds = _.map(fullTabData?.categories, "category_id");

    fieldApproveBulk({
      dealId,
      fieldIds,
      isApproved: !areAllApproved,
    })
      .then(() => {
        sectionApproveBulk({
          dealId,
          sectionIds,
          isApproved: !areAllApproved,
        }).then(() => {
          return Promise.all(
            categoryIds.map((categoryId) =>
              toggleCategoryApproval({
                dealId,
                categoryId: categoryId,
                isCompleted: !areAllApproved,
              })
            )
          ).then(() => {
            handlePackageUpdate?.({
              message: `${fullTabData?.name} was successfully ${
                !areAllApproved ? "completed" : "uncompleted"
              }!`,
            });
          });
        });
      })
      .catch(() => {
        toast.errorMessage(DEFAULT_ERROR_MESSAGE);
      });
  }, [areAllApproved, dealId, fullTabData, handlePackageUpdate]);

  const handleTabMarkAllAsSeen = useCallback(() => {
    const fieldIds = _.flatMap(fullTabData?.categories, (category) =>
      _.flatMap(category?.sections, (section) =>
        _.flatMap(section?.fields, "field_id")
      )
    );

    fieldMarkAsSeenBulk({
      dealId,
      packageId,
      fieldIds,
    }).then(() => {
      mutateChecklistWithTabs();
      handlePackageUpdate?.({
        message: `Updated badges for ${fullTabData?.name} have been removed successfully!`,
      });
    });
  }, [
    dealId,
    fullTabData,
    handlePackageUpdate,
    packageId,
    mutateChecklistWithTabs,
  ]);

  return (
    <Link
      replace
      to={reverse(URLS.DEAL_PACKAGE, {
        dealId,
        packageId,
        tabId: tab.id,
      })}
      linkDataTestid={linkDataTestid}
    >
      <Button
        variant={isSelected ? "contained" : "text"}
        color="secondary"
        sx={{
          width: "100%",
          textAlign: "left",
          paddingLeft: 1.5,
          paddingRight: 0,
          borderLeft: isSelected ? `6px solid ${colors.blue100}` : "0",
          minHeight: "38px",
          height: "100%",
          display: "flex",
          justifyContent: "space-between",
        }}
        data-testid={buttonDataTestid}
      >
        {tab.name}
        <Stack direction="row">
          {tab.updated_field_count > 0 && (
            <FieldUpdateIcon
              tooltip={`Tab has ${tab.updated_field_count} field${tab.updated_field_count > 1 ? "s" : ""} updated`}
              numberOfUpdates={tab.updated_field_count}
            />
          )}
          {canSeeActionsMenu && (
            <ButtonDropdown
              variant="text"
              label={<MoreVertIcon />}
              width="auto"
              size="small"
              sx={{ padding: "2px" }}
            >
              <GroupLabel label="status" />

              {_.isEmpty(fullTabData) ? (
                <>
                  <MenuItem>
                    <Text variant="text2">
                      <Skeleton width="132px" />
                    </Text>
                  </MenuItem>
                  <MenuItem>
                    <Text variant="text2">
                      <Skeleton width="132px" />
                    </Text>
                  </MenuItem>
                </>
              ) : (
                <>
                  <MenuItem onClick={handleMarkAsNA}>
                    <Text variant="text2">
                      {areAllNA ? "Revoke N/A status" : "Mark all as N/A"}
                    </Text>
                  </MenuItem>

                  <MenuItem disabled={areAllNA} onClick={handleApprove}>
                    <Text variant="text2">
                      {areAllApproved
                        ? "Revoke done status"
                        : "Mark all as done"}
                    </Text>
                  </MenuItem>

                  <MenuItem onClick={handleTabMarkAllAsSeen}>
                    <Text variant="text2">Remove updated badges</Text>
                  </MenuItem>
                </>
              )}
            </ButtonDropdown>
          )}
        </Stack>
      </Button>
    </Link>
  );
};

export default LeftMenuLink;
